import { useEffect, useRef, useState } from 'react'
type UseInViewOptions = {
  /** Load slightly before the element scrolls into view. */
  rootMargin?: string
  /** Stop observing after the first intersection. */
  once?: boolean
}
/**
 * Tracks whether an element is in (or near) the viewport, so expensive work —
 * WebGL shaders, map tiles, video decoding — can be deferred until it matters.
 */
export function useInView<T extends HTMLElement>({
  rootMargin = '200px',
  once = false,
}: UseInViewOptions = {}) {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setInView(false)
        }
      },
      {
        rootMargin,
      },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [rootMargin, once])
  return {
    ref,
    inView,
  }
}
